import type { ReferenceInput } from '@/types/reference'

import type { SupportedKeys } from './property.type'
import { isPropertyVisibleInView } from './property.visibility'

const toRelevantReferenceKeys = (params: {
  listId?: string
  supportedKeys: SupportedKeys[]
}) => {
  const { listId, supportedKeys } = params
  if (!listId) return new Set<string>()

  return supportedKeys.reduce<Set<string>>((acc, supportedKey) => {
    if (supportedKey.type !== 'LIST') return acc
    const supportedListId = supportedKey.listId ?? supportedKey.id
    if (supportedListId !== listId) return acc
    if (!isPropertyVisibleInView(supportedKey, 'references')) return acc
    acc.add(supportedKey.key)
    return acc
  }, new Set<string>())
}

const dedupeValues = (values: string[]) => {
  const seen = new Set<string>()
  return values.filter((value) => {
    if (!value || seen.has(value)) return false
    seen.add(value)
    return true
  })
}

export const buildOptionReferenceUsageCounts = (params: {
  listId?: string
  supportedKeys: SupportedKeys[]
  references: ReferenceInput[] | undefined
  optionIds?: Iterable<string>
}) => {
  const { listId, supportedKeys, references, optionIds } = params
  const relevantKeys = toRelevantReferenceKeys({ listId, supportedKeys })
  if (
    relevantKeys.size === 0 ||
    !Array.isArray(references) ||
    references.length === 0
  ) {
    return {} as Record<string, number>
  }

  const allowedOptionIds = optionIds ? new Set(optionIds) : undefined
  const counts: Record<string, number> = {}

  references.forEach((reference) => {
    const referencedOptionIds = new Set<string>()

    reference.properties?.forEach((property) => {
      if (!relevantKeys.has(property.key)) return

      property.values?.forEach((value) => {
        if (typeof value !== 'string' || value.length === 0) return
        if (allowedOptionIds && !allowedOptionIds.has(value)) return
        referencedOptionIds.add(value)
      })
    })

    referencedOptionIds.forEach((optionId) => {
      counts[optionId] = (counts[optionId] ?? 0) + 1
    })
  })

  return counts
}

export const buildOptionReplacementPlan = (params: {
  listId?: string
  supportedKeys: SupportedKeys[]
  references: ReferenceInput[] | undefined
  sourceOptionId: string
  replacementOptionId: string
}) => {
  const {
    listId,
    supportedKeys,
    references,
    sourceOptionId,
    replacementOptionId,
  } = params

  if (
    !sourceOptionId ||
    !replacementOptionId ||
    sourceOptionId === replacementOptionId
  ) {
    return {
      updates: [] as ReferenceInput[],
      affectedReferenceIds: [] as string[],
    }
  }

  const relevantKeys = toRelevantReferenceKeys({ listId, supportedKeys })

  if (
    relevantKeys.size === 0 ||
    !Array.isArray(references) ||
    references.length === 0
  ) {
    return {
      updates: [] as ReferenceInput[],
      affectedReferenceIds: [] as string[],
    }
  }

  const updates: ReferenceInput[] = []
  const affectedReferenceIds: string[] = []

  references.forEach((reference) => {
    let didChange = false

    const nextProperties = reference.properties?.map((property) => {
      if (!relevantKeys.has(property.key)) return property
      if (!property.values?.includes(sourceOptionId)) return property

      didChange = true

      return {
        ...property,
        values: dedupeValues(
          property.values.map((value) =>
            value === sourceOptionId ? replacementOptionId : value,
          ),
        ),
      }
    })

    if (!didChange) return

    updates.push({
      ...reference,
      properties: nextProperties,
    })
    affectedReferenceIds.push(reference.id)
  })

  return {
    updates,
    affectedReferenceIds,
  }
}
